import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const capitalizeFirst = (s) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

export const pageLqUrl = (id, n) => { const u = pageUrl(id, n); return `${u}${u.includes("?") ? "&" : "?"}lq=1`; };
export const coverUrl = (id) => `${API}/editions/${id}/cover`;
export const coverThumbUrl = (id) => `${API}/editions/${id}/cover?thumb=1`;
export const coverLogoUrl = (id) => `${API}/editions/${id}/cover?logo=1`;
export const pageUrl = (id, n) => {
  const t = getToken();
  return `${API}/editions/${id}/page/${n}${t ? `?token=${encodeURIComponent(t)}` : ""}`;
};
export const avatarUrl = (id, v = "") => `${API}/users/${id}/avatar${v ? `?v=${encodeURIComponent(v)}` : ""}`;
export const bannerImageUrl = (id) => `${API}/banners/${id}/image`;
export const bannerHistoryImageUrl = (id) => `${API}/banners/history/${id}/image`;
export const fileUrl = (id, download = false) => {
  const t = getToken();
  const params = [];
  if (download) params.push("download=1");
  if (t) params.push(`token=${encodeURIComponent(t)}`);
  return `${API}/editions/${id}/file${params.length ? `?${params.join("&")}` : ""}`;
};

// Device detection. iPadOS reports as "Macintosh" with touch points > 1.
const isIOSDevice = () => {
  const ua = (navigator.userAgent || "").toLowerCase();
  return (
    /iphone|ipad|ipod/.test(ua) ||
    (/macintosh/.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1)
  );
};

const HIDE_DL_KEY = "appresse_hide_download_info";

// Silent download via a hidden iframe. The main page is never navigated, so
// there is NEVER a blank page/tab — works on desktop and Android.
const iframeDownload = (url) => {
  const f = document.createElement("iframe");
  f.style.display = "none";
  f.setAttribute("aria-hidden", "true");
  f.src = url;
  document.body.appendChild(f);
  setTimeout(() => {
    try {
      f.remove();
    } catch (e) {}
  }, 120000);
};

// Download a magazine with a proper loading animation and no dead-end.
//  - We pre-fetch the PDF inside the app showing a progress %, so the user sees
//    a loading state instead of a blank page.
//  - Desktop / Android: the file is then saved via a blob link (instant, no blank).
//  - iOS / iPadOS: opening the file externally traps the user in the native PDF
//    viewer with no controls. Instead we present the native iOS share sheet
//    (« Enregistrer dans Fichiers ») from a button tap, keeping the user in the app.
export const downloadFile = async (id, title = "") => {
  const ios = isIOSDevice();
  const hidden = localStorage.getItem(HIDE_DL_KEY) === "1";
  const emit = (detail) =>
    window.dispatchEvent(new CustomEvent("appresse:download-info", { detail }));
  const filename =
    `${(title || "revue").replace(/[^\w\-. ]+/g, "_").trim() || "revue"}.pdf`;

  // Non-iOS users who opted out of the guide: silent, instant download.
  if (hidden && !ios) {
    iframeDownload(fileUrl(id, true));
    return;
  }

  // iOS / iPadOS: the primary action is sharing the APP PRESSE card (a link),
  // which needs NO file download. So we open the sheet instantly and only fetch
  // the ~50 MB PDF lazily if the user explicitly chooses to save it in Files.
  if (ios) {
    emit({
      phase: "ready-ios",
      title,
      filename,
      shareUrl: withRef(`${window.location.origin}/api/share/${id}`),
      fileHref: fileUrl(id, true),
    });
    return;
  }

  emit({ phase: "loading", id, title, ios, progress: 0 });

  let blob;
  const MAX_TRIES = 3;
  for (let attempt = 1; attempt <= MAX_TRIES; attempt++) {
    try {
      const res = await fetch(fileUrl(id, true));
      if (!res.ok) throw new Error("http " + res.status);
      const total = Number(res.headers.get("content-length")) || 0;
      if (res.body && res.body.getReader) {
        const reader = res.body.getReader();
        const chunks = [];
        let received = 0;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.length;
          emit({
            phase: "loading",
            id,
            title,
            ios,
            progress: total ? Math.min(99, Math.round((received / total) * 100)) : 0,
          });
        }
        blob = new Blob(chunks, { type: "application/pdf" });
      } else {
        blob = await res.blob();
      }
      break; // success
    } catch (e) {
      if (attempt === MAX_TRIES) {
        emit({ phase: "error", id, title, ios });
        return;
      }
      // Transient network drop → wait and retry automatically.
      await new Promise((r) => setTimeout(r, 800 * attempt));
      emit({ phase: "loading", id, title, ios, progress: 0 });
    }
  }

  const objectUrl = URL.createObjectURL(blob);

  // Desktop / Android: save immediately from the in-memory blob (no blank page).
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    try {
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {}
  }, 5000);
  emit(hidden ? { phase: "close" } : { phase: "done", title });
};

// ---- Admin key (ingest/admin panel) ----
export const ADMIN_KEY_STORE = "appresse_admin_key";
export const getAdminKey = () => localStorage.getItem(ADMIN_KEY_STORE) || "";
export const setAdminKey = (k) => localStorage.setItem(ADMIN_KEY_STORE, k);
export const clearAdminKey = () => localStorage.removeItem(ADMIN_KEY_STORE);
export const adminHeaders = () => ({ "X-Admin-Key": getAdminKey() });

// ---- User JWT token ----
const TOKEN_STORE = "appresse_token";
export const getToken = () => localStorage.getItem(TOKEN_STORE) || "";
export const setToken = (t) => {
  localStorage.setItem(TOKEN_STORE, t);
  axios.defaults.headers.common.Authorization = `Bearer ${t}`;
};
export const clearToken = () => {
  localStorage.removeItem(TOKEN_STORE);
  delete axios.defaults.headers.common.Authorization;
};
const _t = getToken();
if (_t) axios.defaults.headers.common.Authorization = `Bearer ${_t}`;

export const api = {
  categories: () => axios.get(`${API}/categories`, { params: { _: Date.now() } }).then((r) => r.data),
  editions: (params) => axios.get(`${API}/editions`, { params: { ...(params || {}), _: Date.now() } }).then((r) => r.data),
  home: () => axios.get(`${API}/home`, { params: { _: Date.now() } }).then((r) => r.data),
  homeVersion: () => axios.get(`${API}/home/version`, { params: { _: Date.now() } }).then((r) => r.data),
  featured: () => axios.get(`${API}/editions/featured`, { params: { _: Date.now() } }).then((r) => r.data),
  edition: (id) => axios.get(`${API}/editions/${id}`).then((r) => r.data),
  // monetization
  monetizationPublic: () => axios.get(`${API}/monetization`).then((r) => r.data),
  getMonetization: () => axios.get(`${API}/admin/monetization`, { headers: adminHeaders() }).then((r) => r.data),
  saveMonetization: (cfg) => axios.put(`${API}/admin/monetization`, cfg, { headers: adminHeaders() }).then((r) => r.data),
  // subscriptions & groups (user)
  checkoutSubscription: (plan, billing) => axios.post(`${API}/subscriptions/checkout`, { plan, billing }).then((r) => r.data),
  paymentStatus: (sessionId) => axios.get(`${API}/payments/status/${sessionId}`).then((r) => r.data),
  mySubscription: () => axios.get(`${API}/me/subscription`).then((r) => r.data),
  savePaidInfo: (body) => axios.post(`${API}/me/paid-info`, body).then((r) => r.data),
  myPoints: () => axios.get(`${API}/me/points`).then((r) => r.data),
  regenerateGroupLink: () => axios.post(`${API}/groups/regenerate-link`, {}).then((r) => r.data),
  groupJoinInfo: (token) => axios.get(`${API}/groups/join/${token}`).then((r) => r.data),
  groupJoin: (token) => axios.post(`${API}/groups/join/${token}`, {}).then((r) => r.data),
  removeGroupMember: (memberId) => axios.post(`${API}/groups/remove-member`, { member_id: memberId }).then((r) => r.data),
  changePlan: (plan, billing) => axios.post(`${API}/subscriptions/change-plan`, { plan, billing }).then((r) => r.data),
  changeQuote: (plan, billing) => axios.get(`${API}/subscriptions/change-quote`, { params: { plan, billing } }).then((r) => r.data),
  cancelSubscription: () => axios.post(`${API}/subscriptions/cancel`, {}).then((r) => r.data),
  // subscriptions & groups (admin)
  adminSubscriptions: (params) => axios.get(`${API}/admin/subscriptions`, { headers: adminHeaders(), params }).then((r) => r.data),
  adminProspects: (params) => axios.get(`${API}/admin/prospects`, { headers: adminHeaders(), params }).then((r) => r.data),
  adminProspectEvents: (id) => axios.get(`${API}/admin/prospects/${id}/events`, { headers: adminHeaders() }).then((r) => r.data),
  adminProspectSettings: () => axios.get(`${API}/admin/prospects/settings`, { headers: adminHeaders() }).then((r) => r.data),
  adminSaveProspectSettings: (b) => axios.put(`${API}/admin/prospects/settings`, b, { headers: adminHeaders() }).then((r) => r.data),
  adminProspectSync: (full, apply) => axios.post(`${API}/admin/prospects/sync`, null, { headers: adminHeaders(), params: { full: full ? 1 : 0, apply: apply ? 1 : 0 } }).then((r) => r.data),
  adminProspectImportFile: (src, apply) => { const fd = new FormData(); if (src.text != null) fd.append("text", src.text); else fd.append("file", src.file || src); fd.append("apply", apply ? "1" : "0"); return axios.post(`${API}/admin/prospects/import-file`, fd, { headers: adminHeaders() }).then((r) => r.data); },
  adminProspectImportBatch: (files, apply, onProgress) => { const fd = new FormData(); files.forEach((f) => fd.append("files", f)); fd.append("apply", apply ? "1" : "0"); return axios.post(`${API}/admin/prospects/import-batch`, fd, { headers: adminHeaders(), onUploadProgress: (e) => onProgress && e.total && onProgress(Math.round((e.loaded / e.total) * 100)) }).then((r) => r.data); },
  adminProspectImportJob: (id) => axios.get(`${API}/admin/prospects/import-jobs/${id}`, { headers: adminHeaders() }).then((r) => r.data),
  adminProspectRunReport: (id) => axios.get(`${API}/admin/prospects/sync/runs/${id}/report.xlsx`, { headers: adminHeaders(), responseType: "blob" }).then((r) => r.data),
  adminProspectRuns: () => axios.get(`${API}/admin/prospects/sync/runs`, { headers: adminHeaders() }).then((r) => r.data),
  adminProspectExcluded: (id) => axios.get(`${API}/admin/prospects/sync/runs/${id}/excluded`, { headers: adminHeaders() }).then((r) => r.data),
  adminProspectStats: () => axios.get(`${API}/admin/prospects-stats`, { headers: adminHeaders() }).then((r) => r.data),
  adminCampaignAnalytics: (params) => axios.get(`${API}/admin/campaigns/analytics`, { headers: adminHeaders(), params }).then((r) => r.data),
  adminCampaigns: () => axios.get(`${API}/admin/campaigns`, { headers: adminHeaders() }).then((r) => r.data),
  adminCampaignEstimate: (segment) => axios.post(`${API}/admin/campaigns/estimate`, { segment }, { headers: adminHeaders() }).then((r) => r.data),
  adminCreateCampaign: (b) => axios.post(`${API}/admin/campaigns`, b, { headers: adminHeaders() }).then((r) => r.data),
  adminTestCampaign: (id, email) => axios.post(`${API}/admin/campaigns/${id}/test`, { email }, { headers: adminHeaders() }).then((r) => r.data),
  adminLaunchCampaign: (id) => axios.post(`${API}/admin/campaigns/${id}/launch`, null, { headers: adminHeaders() }).then((r) => r.data),
  adminPauseCampaign: (id) => axios.post(`${API}/admin/campaigns/${id}/pause`, null, { headers: adminHeaders() }).then((r) => r.data),
  adminTemplates: (params) => axios.get(`${API}/admin/email-templates`, { headers: adminHeaders(), params }).then((r) => r.data),
  adminTemplate: (id) => axios.get(`${API}/admin/email-templates/${id}`, { headers: adminHeaders() }).then((r) => r.data),
  adminCreateTemplate: (b) => axios.post(`${API}/admin/email-templates`, b, { headers: adminHeaders() }).then((r) => r.data),
  adminSaveTemplate: (id, b) => axios.put(`${API}/admin/email-templates/${id}`, b, { headers: adminHeaders() }).then((r) => r.data),
  adminDuplicateTemplate: (id, name) => axios.post(`${API}/admin/email-templates/${id}/duplicate`, { name }, { headers: adminHeaders() }).then((r) => r.data),
  adminArchiveTemplate: (id) => axios.post(`${API}/admin/email-templates/${id}/archive`, null, { headers: adminHeaders() }).then((r) => r.data),
  adminDeleteTemplate: (id) => axios.delete(`${API}/admin/email-templates/${id}`, { headers: adminHeaders() }).then((r) => r.data),
  adminTemplateVersions: (id) => axios.get(`${API}/admin/email-templates/${id}/versions`, { headers: adminHeaders() }).then((r) => r.data),
  adminRestoreTemplate: (id, v) => axios.post(`${API}/admin/email-templates/${id}/restore/${v}`, null, { headers: adminHeaders() }).then((r) => r.data),
  adminRenderTemplate: (template) => axios.post(`${API}/admin/email-templates/render`, { template, base: window.location.origin }, { headers: adminHeaders() }).then((r) => r.data),
  adminTestTemplate: (template, email) => axios.post(`${API}/admin/email-templates/test`, { template, email, base: window.location.origin }, { headers: adminHeaders() }).then((r) => r.data),
  adminMedia: () => axios.get(`${API}/admin/media`, { headers: adminHeaders() }).then((r) => r.data),
  adminUploadMedia: (file) => { const f = new FormData(); f.append("file", file); return axios.post(`${API}/admin/media`, f, { headers: adminHeaders() }).then((r) => r.data); },
  adminDeleteMedia: (id) => axios.delete(`${API}/admin/media/${id}`, { headers: adminHeaders() }).then((r) => r.data),
  legalPage: (slug) => axios.get(`${API}/legal/${slug}`).then((r) => r.data),
  adminSaveLegal: (slug, b) => axios.put(`${API}/admin/legal/${slug}`, b, { headers: adminHeaders() }).then((r) => r.data),
  adminCampaignReport: (id) => axios.get(`${API}/admin/campaigns/${id}/report`, { headers: adminHeaders() }).then((r) => r.data),
  adminCampaignRecipients: (id, params) => axios.get(`${API}/admin/campaigns/${id}/recipients`, { headers: adminHeaders(), params }).then((r) => r.data),
  adminCampaignExport: (id, filter) => axios.get(`${API}/admin/campaigns/${id}/export.xlsx`, { headers: adminHeaders(), params: { filter }, responseType: "blob" }).then((r) => r.data),
  adminStopCampaign: (id) => axios.post(`${API}/admin/campaigns/${id}/stop`, null, { headers: adminHeaders() }).then((r) => r.data),
  adminRevenue: (params) => axios.get(`${API}/admin/revenue`, { headers: adminHeaders(), params }).then((r) => r.data),
  adminRevenueSync: (full) => axios.post(`${API}/admin/revenue/sync`, null, { headers: adminHeaders(), params: { full: full ? 1 : 0 } }).then((r) => r.data),
  adminRevenueClient: (key) => axios.get(`${API}/admin/revenue/client/${encodeURIComponent(key)}`, { headers: adminHeaders() }).then((r) => r.data),
  adminRevenueSub: (id) => axios.get(`${API}/admin/revenue/subscription/${encodeURIComponent(id)}`, { headers: adminHeaders() }).then((r) => r.data),
  adminStripeHealth: () => axios.get(`${API}/admin/stripe/health`, { headers: adminHeaders() }).then((r) => r.data),
  adminStripeDeactivate: (id) => axios.post(`${API}/admin/stripe/deactivate/${id}`, null, { headers: adminHeaders() }).then((r) => r.data),
  adminRevenuePdf: (params) => axios.get(`${API}/admin/revenue/pdf`, { headers: adminHeaders(), params, responseType: "blob" }).then((r) => r.data),
  adminSubscriptionDetail: (id) => axios.get(`${API}/admin/subscriptions/${id}`, { headers: adminHeaders() }).then((r) => r.data),
  adminUserPoints: (id) => axios.get(`${API}/admin/users/${id}/points`, { headers: adminHeaders() }).then((r) => r.data),
  adminAdjustPoints: (id, delta, reason) => axios.post(`${API}/admin/users/${id}/points`, { delta, reason }, { headers: adminHeaders() }).then((r) => r.data),
  adminSubscriptionAction: (id, payload) => axios.post(`${API}/admin/subscriptions/${id}/action`, payload, { headers: adminHeaders() }).then((r) => r.data),
  // admin
  adminEditions: (params) => axios.get(`${API}/admin/editions`, { headers: adminHeaders(), params }).then((r) => r.data),  setPublished: (id, published) =>
    axios.patch(`${API}/admin/editions/${id}/publish`, { published }, { headers: adminHeaders() }).then((r) => r.data),
  setFeatured: (id, featured) =>
    axios.patch(`${API}/admin/editions/${id}/featured`, { featured }, { headers: adminHeaders() }).then((r) => r.data),
  setFeaturedToday: (id, on = true) =>
    axios.patch(`${API}/admin/editions/${id}/featured`, on ? { one_day: true } : { today_off: true }, { headers: adminHeaders() }).then((r) => r.data),
  setCategory: (id, category) =>
    axios.patch(`${API}/admin/editions/${id}/category`, { category }, { headers: adminHeaders() }).then((r) => r.data),
  analyzeEditions: () =>
    axios.get(`${API}/admin/editions/analyze`, { headers: adminHeaders() }).then((r) => r.data),
  autoCategorize: () =>
    axios.post(`${API}/admin/editions/auto-categorize`, {}, { headers: adminHeaders() }).then((r) => r.data),
  autoCategorizeStatus: () =>
    axios.get(`${API}/admin/editions/auto-categorize/status`, { headers: adminHeaders() }).then((r) => r.data),
  findDuplicates: () =>
    axios.get(`${API}/admin/editions/duplicates`, { headers: adminHeaders() }).then((r) => r.data),
  mergeAllDuplicates: () => axios.post(`${API}/admin/editions/merge-all`, {}, { headers: adminHeaders() }).then((r) => r.data),
  mergeEditions: (keep_id, remove_ids) =>
    axios.post(`${API}/admin/editions/merge`, { keep_id, remove_ids }, { headers: adminHeaders() }).then((r) => r.data),
  ambassadorNudge: (action) =>
    axios.post(`${API}/ambassador/nudge`, { action }).then((r) => r.data),
  setInternational: (id, international) =>
    axios.patch(`${API}/admin/editions/${id}/international`, { international }, { headers: adminHeaders() }).then((r) => r.data),
  setShowLast: (id, show_last) =>
    axios.patch(`${API}/admin/editions/${id}/show-last`, { show_last }, { headers: adminHeaders() }).then((r) => r.data),
  notifyFavorites: (id) =>
    axios.post(`${API}/admin/editions/${id}/notify-favorites`, {}, { headers: adminHeaders() }).then((r) => r.data),
  setPubDate: (id, publication_date) =>
    axios.patch(`${API}/admin/editions/${id}/publication-date`, { publication_date }, { headers: adminHeaders() }).then((r) => r.data),
  detectLanguages: (force) =>
    axios.post(`${API}/admin/editions/detect-languages`, {}, { headers: adminHeaders(), params: force ? { force: 1 } : undefined }).then((r) => r.data),
  prerenderMissing: () =>
    axios.post(`${API}/admin/editions/prerender-missing`, {}, { headers: adminHeaders() }).then((r) => r.data),
  rerenderOversized: (days = 0) =>
    axios.post(`${API}/admin/editions/rerender-oversized`, {}, { headers: adminHeaders(), params: days ? { days } : {} }).then((r) => r.data),
  jobProgress: (name) =>
    axios.get(`${API}/admin/jobs/${name}/progress`, { headers: adminHeaders() }).then((r) => r.data),
  rerenderPages: (id) =>
    axios.post(`${API}/admin/editions/${id}/rerender-pages`, {}, { headers: adminHeaders() }).then((r) => r.data),
  renderProgress: (id) =>
    axios.get(`${API}/admin/editions/${id}/render-progress`, { headers: adminHeaders() }).then((r) => r.data),
  verifyAdmin: (key) =>
    axios.get(`${API}/admin/verify`, { headers: { "X-Admin-Key": key } }).then((r) => r.data),
  ingestInfo: () =>
    axios.get(`${API}/admin/ingest-info`, { headers: adminHeaders() }).then((r) => r.data),
  deleteEdition: (id) =>
    axios.delete(`${API}/admin/editions/${id}`, { headers: adminHeaders() }).then((r) => r.data),
  upload: (formData, onProgress) =>
    axios
      .post(`${API}/admin/editions`, formData, {
        headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => onProgress && onProgress(Math.round((e.loaded * 100) / (e.total || 1))),
      })
      .then((r) => r.data),
  uploadChunk: (uploadId, index, blob, onProgress) => {
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("index", index);
    fd.append("chunk", blob, "chunk");
    return axios.post(`${API}/admin/upload/chunk`, fd, {
      headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => onProgress && onProgress(e.loaded || 0),
    }).then((r) => r.data);
  },
  uploadComplete: (payload) => {
    const fd = new FormData();
    Object.entries(payload).forEach(([k, v]) => { if (v != null) fd.append(k, v); });
    return axios.post(`${API}/admin/upload/complete`, fd, {
      headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  uploadStatus: (uploadId) =>
    axios.get(`${API}/admin/upload/status/${uploadId}`, { headers: adminHeaders() }).then((r) => r.data),
  // auth
  register: (b) => axios.post(`${API}/auth/register`, b).then((r) => r.data),
  login: (b) => axios.post(`${API}/auth/login`, b).then((r) => r.data),
  me: () => axios.get(`${API}/auth/me`).then((r) => r.data),
  forgotPassword: (email) => axios.post(`${API}/auth/forgot-password`, { email }).then((r) => r.data),
  verifyEmail: (code) => axios.post(`${API}/auth/verify-email`, { code }).then((r) => r.data),
  resendVerification: () => axios.post(`${API}/auth/resend-verification`, {}).then((r) => r.data),
  resetPassword: (token, password) => axios.post(`${API}/auth/reset-password`, { token, password }).then((r) => r.data),
  // favorites / history
  toggleFavorite: (id) => axios.post(`${API}/favorites/${id}`).then((r) => r.data),
  myFavorites: () => axios.get(`${API}/me/favorites`).then((r) => r.data),
  recordHistory: (id) => axios.post(`${API}/history/${id}`).then((r) => r.data),
  myHistory: () => axios.get(`${API}/me/history`).then((r) => r.data),
  // events / tracking
  track: (body) => axios.post(`${API}/track`, body).then((r) => r.data),
  trackPages: (body) => axios.post(`${API}/track/pages`, body).then((r) => r.data),
  pageAnalytics: (id) => axios.get(`${API}/admin/editions/${id}/page-analytics`, { headers: adminHeaders() }).then((r) => r.data),
  setDuration: (evId, duration_s) =>
    axios.patch(`${API}/events/${evId}/duration`, { duration_s }).then((r) => r.data),
  // profile
  updateProfile: (body) => axios.put(`${API}/auth/profile`, body).then((r) => r.data),
  // geo — bundled postal-code -> city database
  geoCountries: () => axios.get(`${API}/geo/countries`).then((r) => r.data),
  geoCity: (postal, country = "FR") =>
    axios.get(`${API}/geo/city`, { params: { postal, country } }).then((r) => r.data),
  uploadAvatar: (formData) =>
    axios.post(`${API}/auth/avatar`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  // admin analytics
  statsOverview: () => axios.get(`${API}/admin/stats/overview`, { headers: adminHeaders() }).then((r) => r.data),
  statsTraffic: (params) => axios.get(`${API}/admin/stats/traffic`, { params, headers: adminHeaders() }).then((r) => r.data),
  trackVisit: () => {
    try {
      const t = localStorage.getItem("appresse_token");
      const day = `${new Date().toISOString().slice(0, 10)}:${t ? 1 : 0}`;
      if (sessionStorage.getItem("appresse_visit_day") === day) return;
      let vid = localStorage.getItem("appresse_vid");
      if (!vid) { vid = (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2)); localStorage.setItem("appresse_vid", vid); }
      axios.post(`${API}/visit`, { vid }, t ? { headers: { Authorization: `Bearer ${t}` } } : {}).then(() => sessionStorage.setItem("appresse_visit_day", day)).catch(() => {});
    } catch { /* ignore */ }
  },
  statsCompare: (params) => axios.get(`${API}/admin/stats/compare`, { params, headers: adminHeaders() }).then((r) => r.data),
  statsBreakdown: (params) => axios.get(`${API}/admin/stats/breakdown`, { params, headers: adminHeaders() }).then((r) => r.data),
  statsHourly: (params) => axios.get(`${API}/admin/stats/hourly`, { params, headers: adminHeaders() }).then((r) => r.data),
  statsTop: (params) => axios.get(`${API}/admin/stats/top`, { params, headers: adminHeaders() }).then((r) => r.data),
  statsPublications: (params) => axios.get(`${API}/admin/stats/publications`, { params, headers: adminHeaders() }).then((r) => r.data),
  statsPublicationReaders: (editionId, params) => axios.get(`${API}/admin/stats/publication/${editionId}/readers`, { params, headers: adminHeaders() }).then((r) => r.data),
  statsDetail: (params) => axios.get(`${API}/admin/stats/detail`, { params, headers: adminHeaders() }).then((r) => r.data),
  adminClients: (params) => axios.get(`${API}/admin/clients`, { params, headers: adminHeaders() }).then((r) => r.data),
  adminClient: (id) => axios.get(`${API}/admin/clients/${id}`, { headers: adminHeaders() }).then((r) => r.data),
  adminClientUpdate: (id, body) => axios.put(`${API}/admin/clients/${id}`, body, { headers: adminHeaders() }).then((r) => r.data),
  adminSetAmbassador: (id, enabled) => axios.post(`${API}/admin/clients/${id}/ambassador`, { enabled }, { headers: adminHeaders() }).then((r) => r.data),
  adminSetClientStatus: (id, status) => axios.post(`${API}/admin/clients/${id}/status`, { status }, { headers: adminHeaders() }).then((r) => r.data),
  adminSetBillingMode: (id, must_pay) => axios.post(`${API}/admin/clients/${id}/billing-mode`, { must_pay }, { headers: adminHeaders() }).then((r) => r.data),
  adminDeleteClient: (id, confirm) => axios.delete(`${API}/admin/clients/${id}`, { params: { confirm }, headers: adminHeaders() }).then((r) => r.data),
  adminDeletePreview: (id) => axios.get(`${API}/admin/clients/${id}/delete-preview`, { headers: adminHeaders() }).then((r) => r.data),
  adminClientActivity: (id, params) => axios.get(`${API}/admin/clients/${id}/activity`, { params, headers: adminHeaders() }).then((r) => r.data),
  exportEvents: (params) =>
    axios.get(`${API}/admin/export`, { params, headers: adminHeaders(), responseType: "blob" }).then((r) => r.data),
  // partners (admin)
  adminPartners: () => axios.get(`${API}/admin/partners`, { headers: adminHeaders() }).then((r) => r.data),
  adminCreatePartner: (b) => axios.post(`${API}/admin/partners`, b, { headers: adminHeaders() }).then((r) => r.data),
  adminPartner: (id) => axios.get(`${API}/admin/partners/${id}`, { headers: adminHeaders() }).then((r) => r.data),
  adminPatchPartner: (id, b) => axios.patch(`${API}/admin/partners/${id}`, b, { headers: adminHeaders() }).then((r) => r.data),
  adminResetPartnerPw: (id) => axios.post(`${API}/admin/partners/${id}/reset-password`, {}, { headers: adminHeaders() }).then((r) => r.data),
  adminDeletePartner: (id) => axios.delete(`${API}/admin/partners/${id}`, { headers: adminHeaders() }).then((r) => r.data),
  // partner portal (self)
  partnerMe: () => axios.get(`${API}/partner/me`).then((r) => r.data),
  partnerStats: () => axios.get(`${API}/partner/stats`).then((r) => r.data),
  partnerClick: (code) => axios.post(`${API}/partners/click`, { code }).then((r) => r.data),
  // push notifications
  pushVapidKey: () => axios.get(`${API}/push/vapid-public-key`).then((r) => r.data),
  pushSubscribe: (subscription, categories) => axios.post(`${API}/push/subscribe`, { subscription, categories }).then((r) => r.data),
  pushUnsubscribe: (endpoint) => axios.post(`${API}/push/unsubscribe`, { endpoint }).then((r) => r.data),
  adminPushSend: (b) => axios.post(`${API}/admin/push/send`, b, { headers: adminHeaders() }).then((r) => r.data),
  adminPushSubscribers: () => axios.get(`${API}/admin/push/subscribers`, { headers: adminHeaders() }).then((r) => r.data),
  adminPushHistory: () => axios.get(`${API}/admin/push/history`, { headers: adminHeaders() }).then((r) => r.data),
  adminPushSettingsGet: () => axios.get(`${API}/admin/push/settings`, { headers: adminHeaders() }).then((r) => r.data),
  adminPushSettingsSet: (auto_new_edition) => axios.post(`${API}/admin/push/settings`, { auto_new_edition }, { headers: adminHeaders() }).then((r) => r.data),
  // chat (ambassador)
  chatConfig: () => axios.get(`${API}/chat/config`).then((r) => r.data),
  chatMessages: () => axios.get(`${API}/chat/messages`).then((r) => r.data),
  chatSend: (formData) => axios.post(`${API}/chat/messages`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  myNotifications: () => axios.get(`${API}/me/notifications`).then((r) => r.data),
  myNotificationsRead: () => axios.post(`${API}/me/notifications/read`).then((r) => r.data),
  adminChatConversations: () => axios.get(`${API}/admin/chat/conversations`, { headers: adminHeaders() }).then((r) => r.data),
  adminChatConversation: (uid) => axios.get(`${API}/admin/chat/conversations/${uid}`, { headers: adminHeaders() }).then((r) => r.data),
  adminChatReply: (uid, body) => axios.post(`${API}/admin/chat/conversations/${uid}/reply`, { body }, { headers: adminHeaders() }).then((r) => r.data),
  adminChatSettingsGet: () => axios.get(`${API}/admin/chat/settings`, { headers: adminHeaders() }).then((r) => r.data),
  adminChatSettingsSet: (b) => axios.post(`${API}/admin/chat/settings`, b, { headers: adminHeaders() }).then((r) => r.data),
  // ad banners (hero carousel)
  publicBanners: () => axios.get(`${API}/banners`).then((r) => r.data),
  bannerEvent: (id, type) => axios.post(`${API}/banners/${id}/event`, { type }).then((r) => r.data),
  adminBanners: () => axios.get(`${API}/admin/banners`, { headers: adminHeaders() }).then((r) => r.data),
  adminCreateBanner: (formData) => axios.post(`${API}/admin/banners`, formData, { headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  adminReplaceBannerImage: (id, formData) => axios.post(`${API}/admin/banners/${id}/image`, formData, { headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  adminPatchBanner: (id, b) => axios.patch(`${API}/admin/banners/${id}`, b, { headers: adminHeaders() }).then((r) => r.data),
  adminDeleteBanner: (id) => axios.delete(`${API}/admin/banners/${id}`, { headers: adminHeaders() }).then((r) => r.data),
  adminBannerStats: (id) => axios.get(`${API}/admin/banners/${id}/stats`, { headers: adminHeaders() }).then((r) => r.data),
  adminBannerHistory: () => axios.get(`${API}/admin/banners/history`, { headers: adminHeaders() }).then((r) => r.data),
  adminSeedBanners: () => axios.post(`${API}/admin/banners/seed-existing`, {}, { headers: adminHeaders() }).then((r) => r.data),
  publicBannerSlots: () => axios.get(`${API}/banners/slots`).then((r) => r.data),
  adminBannerSlots: () => axios.get(`${API}/admin/banners/slots`, { headers: adminHeaders() }).then((r) => r.data),
  // ambassador program
  myAmbassador: () => axios.get(`${API}/me/ambassador`).then((r) => r.data),
  challenge: () => axios.get(`${API}/ambassador/challenge`).then((r) => r.data),
  adminGetChallenge: () => axios.get(`${API}/admin/ambassador/challenge`, { headers: adminHeaders() }).then((r) => r.data),
  adminSetChallenge: (body) => axios.post(`${API}/admin/ambassador/challenge`, body, { headers: adminHeaders() }).then((r) => r.data),
  ambassadorLeaderboard: (period = "week") => axios.get(`${API}/ambassador/leaderboard?period=${period}`).then((r) => r.data),
  trackSiteShare: (channel) => axios.post(`${API}/track/site-share`, { channel }).then((r) => r.data),
  adminAmbassadors: () => axios.get(`${API}/admin/ambassadors`, { headers: adminHeaders() }).then((r) => r.data),
  adminAmbassadorDetail: (uid) => axios.get(`${API}/admin/ambassadors/${uid}`, { headers: adminHeaders() }).then((r) => r.data),
};

// ---- Web Push subscription helper ----
const urlB64ToUint8Array = (base64) => {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const isPushSupported = () =>
  "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export const getPushState = async () => {
  if (!isPushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return sub ? "subscribed" : "default";
  } catch { return "default"; }
};

export const enablePush = async (categories = []) => {
  if (!isPushSupported()) throw new Error("Notifications non supportées sur cet appareil.");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Permission refusée.");
  const reg = await navigator.serviceWorker.ready;
  const { public_key } = await api.pushVapidKey();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(public_key),
    });
  }
  await api.pushSubscribe(sub.toJSON(), categories);
  return true;
};

export const disablePush = async () => {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) { await api.pushUnsubscribe(sub.endpoint); await sub.unsubscribe(); }
  return true;
};

// Code de parrainage du client connecté, ajouté à TOUS les liens partagés.
export const myRefCode = () => { try { return JSON.parse(localStorage.getItem("appresse_user") || "null")?.ref_code || ""; } catch { return ""; } };
export const withRef = (url) => {
  const code = myRefCode();
  if (!code || !url) return url;
  try { const u = new URL(url, window.location.origin); if (!u.searchParams.get("ref")) u.searchParams.set("ref", code); return u.toString(); } catch { return url; }
};

// Referral (?ref=CODE) — first-touch capture stored for later signup attribution
export const REF_STORE = "appresse_ref";
export const getRef = () => localStorage.getItem(REF_STORE) || "";
export const captureRef = () => {
  try {
    const pros = new URLSearchParams(window.location.search).get("pros");
    if (pros && pros !== "test") localStorage.setItem("appresse_pros", pros);
    const code = new URLSearchParams(window.location.search).get("ref");
    if (!code) return;
    if (!localStorage.getItem(REF_STORE)) localStorage.setItem(REF_STORE, code.toUpperCase());
    const seen = sessionStorage.getItem("appresse_ref_click");
    if (seen !== code) {
      sessionStorage.setItem("appresse_ref_click", code);
      api.partnerClick(code).catch(() => {});
    }
  } catch {}
};

export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const formatBytes = (bytes) => {
  if (!bytes) return "—";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} Mo` : `${(bytes / 1024).toFixed(0)} Ko`;
};

export const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

// Derive the publication "brand" (newspaper/magazine name) from an edition title,
// stripping any trailing date / weekday so all issues of the same title group together.
export const brandOf = (title) => {
  let s = (title || "").normalize("NFC");
  // Unify apostrophe glyphs so "L'Équipe" variants collapse together.
  s = s.replace(/[’`´]/g, "'");
  // Keep only the part before a separator (•, |, –, —, ·).
  s = s.split(/[•|–—·]/)[0];
  // Drop a trailing duplicate marker like "(1)" / "(2)".
  s = s.replace(/\s*\(\d+\)\s*$/g, "");
  // Drop trailing flag / pictograph emojis.
  s = s.replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}\u{200D}]+/gu, " ");
  // Drop a trailing issue number ("N1737", "N°100", "Nr.86", "V22 N35", "HS N.1", "N3238 C1").
  s = s.replace(/\s*(?:N°|N|No|Nr\.?|V)\s*°?\s*\.?\s*\d[\d.\s]*.*$/i, "");
  // Drop a trailing month/season (possibly a range) followed by a year.
  s = s.replace(/\s*(?:\b(?:janvier|février|fevrier|mars|avril|mai|juin|juillet|août|aout|septembre|octobre|novembre|décembre|decembre|printemps|été|ete|automne|hiver)\b[\s,/–-]*)+\d{4}.*$/i, "");
  // Drop a trailing full date written with numbers (22 09 2026 / 22/09/2026 / 2026-09-22).
  s = s.replace(/\s*\d{1,2}[\s/.-]\d{1,2}[\s/.-]\d{2,4}.*$/, "");
  s = s.replace(/\s*\d{4}[-/]\d{1,2}[-/]\d{1,2}.*$/, "");
  // Drop a trailing "<day> <month> <year>" written out ("25 Septembre 2026").
  s = s.replace(/\s*\d{1,2}\s+[A-Za-zÀ-ÿ]+\s+\d{4}.*$/i, "");
  // Drop a weekday (with an optional connector) and everything after it.
  s = s.replace(/\s*(?:\b(?:du|de|des|d'|le|la|les|au|aux)\b\s*)?(lundi|mardi|mercredi|jeudi|vendredi|samedi|dimanche)\b.*$/i, "");
  // Drop a trailing stray connector left over ("Le Figaro du" → "Le Figaro").
  s = s.replace(/\s+\b(du|de|des|d'|au|aux|le|la|les)\b\s*$/i, "");
  s = s.replace(/\s*[-–—:,]\s*$/, "");
  s = s.replace(/\s{2,}/g, " ");
  return s.trim() || (title || "").trim();
};

// Normalized matching key for a brand: accent-folded, lowercased, apostrophes and
// punctuation turned into spaces. Lets spelling variants of the SAME publication
// group together ("L'Équipe" = "L Equipe", "Les Échos" = "Les Echos", "corse matin"
// = "Corse Matin"). Use for grouping/matching; use brandOf for the display label.
export const brandKey = (title) =>
  brandOf(title)
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export const formatApiError = (detail) => {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

// ---- IndexedDB (shared with service worker for Web Share Target) ----
const IDB_DB = "appresse-config";
const IDB_STORE = "kv";
function openIdb() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(IDB_DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(IDB_STORE);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}
export async function idbSet(key, value) {
  const db = await openIdb();
  return new Promise((res, rej) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.objectStore(IDB_STORE).put(value, key);
    tx.oncomplete = () => res(true);
    tx.onerror = () => rej(tx.error);
  });
}
export async function idbGet(key) {
  const db = await openIdb();
  return new Promise((res, rej) => {
    const tx = db.transaction(IDB_STORE, "readonly");
    const g = tx.objectStore(IDB_STORE).get(key);
    g.onsuccess = () => res(g.result);
    g.onerror = () => rej(g.error);
  });
}
export async function activateShareReception(ingestUrl) {
  await idbSet("ingestUrl", ingestUrl);
  return true;
}
export async function isShareActivated() {
  return Boolean(await idbGet("ingestUrl"));
}
